import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  ShieldCheck, Zap, Video, MapPin, Sparkles, Tablet, BookOpen,
  ArrowRight, Radio, Lock, ChevronRight, Menu,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1762846700143-4f3a47400986?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const FEAT_META = [
  { icon: Zap, span: "lg:col-span-7", accent: "text-blue-400" },
  { icon: Video, span: "lg:col-span-5", accent: "text-emerald-400" },
  { icon: MapPin, span: "lg:col-span-4", accent: "text-cyan-400" },
  { icon: Sparkles, span: "lg:col-span-4", accent: "text-amber-400" },
  { icon: ShieldCheck, span: "lg:col-span-4", accent: "text-blue-400" },
  { icon: Tablet, span: "lg:col-span-6", accent: "text-cyan-400" },
  { icon: BookOpen, span: "lg:col-span-6", accent: "text-emerald-400" },
];

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.12 } } };
const item = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } } };

export default function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const features = t("landing.features.items", { returnObjects: true }) || [];
  const steps = t("landing.workflow.steps", { returnObjects: true }) || [];
  const badges = t("landing.security.badges", { returnObjects: true }) || [];
  const navLinks = [
    { key: "features", href: "#functies" },
    { key: "workflow", href: "#werkwijze" },
    { key: "security", href: "#veiligheid" },
    { key: "devices", href: "#pwa" },
  ];

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#030712] text-slate-100 antialiased">
      {/* ambient background */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
        <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full blur-[130px]" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.28) 0%, transparent 70%)" }} />
        <div className="absolute bottom-0 right-0 h-[420px] w-[520px] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, rgba(16,185,129,0.14) 0%, transparent 70%)" }} />
      </div>

      {/* Navbar */}
      <header data-testid="landing-navbar" className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? "border-b border-white/10 bg-slate-950/80 backdrop-blur-xl" : "border-b border-transparent"}`}>
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-12">
          <a href="#top" className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 shadow-lg shadow-blue-600/30">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-base font-bold tracking-tight">DispatchMe</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400">Meldkamer C2</div>
            </div>
          </a>
          <nav className="hidden items-center gap-8 md:flex">
            {navLinks.map((l) => (
              <a key={l.key} href={l.href} data-testid={`landing-nav-${l.key}`} className="text-sm font-medium text-slate-400 transition-colors hover:text-white">
                {t(`landing.nav.${l.key}`)}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div data-testid="landing-lang-toggle"><LanguageSwitcher dark /></div>
            <button data-testid="landing-nav-login-button" onClick={() => navigate("/login")}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition-all hover:bg-blue-500 hover:shadow-blue-500/30">
              {t("landing.nav.login")} <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative z-10 mx-auto max-w-7xl px-4 pb-16 pt-32 sm:px-6 lg:grid lg:grid-cols-2 lg:gap-12 lg:px-12 lg:pt-40">
        <motion.div variants={container} initial="hidden" animate="show" className="flex flex-col justify-center">
          <motion.span variants={item} className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.15em] text-blue-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 live-dot" /> {t("landing.hero.badge")}
          </motion.span>
          <motion.h1 variants={item} className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            {t("landing.hero.headline")}
          </motion.h1>
          <motion.p variants={item} className="mt-6 max-w-xl text-lg leading-relaxed text-slate-400">
            {t("landing.hero.tagline")}
          </motion.p>
          <motion.div variants={item} className="mt-9 flex flex-wrap items-center gap-3">
            <button data-testid="hero-primary-cta" onClick={() => navigate("/login")}
              className="group inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-xl shadow-blue-600/25 transition-all hover:bg-blue-500 hover:shadow-blue-500/40">
              {t("landing.hero.primaryCta")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a data-testid="hero-secondary-cta" href="#functies"
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-semibold text-slate-200 backdrop-blur transition-colors hover:bg-white/10">
              {t("landing.hero.secondaryCta")}
            </a>
          </motion.div>
        </motion.div>

        {/* Interactive C2 preview widget */}
        <motion.div initial={{ opacity: 0, scale: 0.96, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
          data-testid="landing-hero-preview" className="mt-12 lg:mt-0">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 shadow-2xl shadow-black/50 backdrop-blur-xl">
            <div className="relative h-40 overflow-hidden">
              <img src={HERO_IMG} alt="Command center" className="h-full w-full object-cover opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
              <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-black/50 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-emerald-300 backdrop-blur">
                <Radio className="h-3 w-3" /> {t("landing.preview.label")}
              </span>
            </div>
            <div className="space-y-3 p-5">
              <PreviewRow label={t("landing.preview.incident")} value="Brand · Gebouw A" dot="bg-red-500" />
              <PreviewRow label={t("landing.preview.priority")} value="PRIO 1" mono />
              <PreviewRow label={t("landing.preview.video")} value={t("landing.preview.online")} dot="bg-emerald-500" />
              <PreviewRow label={t("landing.preview.eta")} value="03:42" mono />
              <div className="flex items-center justify-between border-t border-white/10 pt-3">
                <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-emerald-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 live-dot" /> {t("landing.preview.connected")}
                </span>
                <span className="font-mono text-[11px] text-slate-500">ws · 0.1s</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Features */}
      <Section id="functies" title={t("landing.features.title")} subtitle={t("landing.features.subtitle")}>
        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}
          className="grid grid-cols-1 gap-5 lg:grid-cols-11">
          {(Array.isArray(features) ? features : []).map((f, i) => {
            const M = FEAT_META[i] || FEAT_META[0];
            const Icon = M.icon;
            return (
              <motion.div key={i} variants={item} whileHover={{ y: -6 }} data-testid={`landing-feature-${i}`}
                className={`group rounded-2xl border border-white/10 bg-slate-900/50 p-6 backdrop-blur-sm transition-colors hover:border-blue-500/40 hover:bg-slate-800/60 ${M.span}`}>
                <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 ${M.accent}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-lg font-semibold text-white">{f.title}</h3>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-blue-300">
                  {f.highlight}
                </span>
              </motion.div>
            );
          })}
        </motion.div>
      </Section>

      {/* Workflow */}
      <Section id="werkwijze" title={t("landing.workflow.title")} subtitle={t("landing.workflow.subtitle")}>
        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {(Array.isArray(steps) ? steps : []).map((s, i) => (
            <motion.div key={i} variants={item} data-testid={`landing-step-${i}`}
              className="relative rounded-2xl border border-white/10 bg-slate-900/50 p-6 backdrop-blur-sm">
              <div className="font-mono text-4xl font-black text-blue-500/30">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="mt-3 font-display text-lg font-semibold text-white">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
              {i < steps.length - 1 && <ChevronRight className="absolute -right-3 top-1/2 hidden h-6 w-6 -translate-y-1/2 text-blue-500/40 lg:block" />}
            </motion.div>
          ))}
        </motion.div>
      </Section>

      {/* Security */}
      <Section id="veiligheid" title={t("landing.security.title")} subtitle={t("landing.security.subtitle")}>
        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}
          className="flex flex-wrap justify-center gap-3">
          {(Array.isArray(badges) ? badges : []).map((b, i) => (
            <motion.span key={i} variants={item} data-testid={`landing-badge-${i}`}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-900/60 px-5 py-3 text-sm font-medium text-slate-200 backdrop-blur">
              <Lock className="h-4 w-4 text-emerald-400" /> {b}
            </motion.span>
          ))}
        </motion.div>
      </Section>

      {/* CTA + Footer */}
      <footer id="pwa" className="relative z-10 border-t border-white/10 bg-slate-950/60">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12">
          <div className="rounded-3xl border border-blue-500/20 bg-gradient-to-br from-blue-600/15 to-slate-900/40 p-10 text-center backdrop-blur">
            <h2 className="mx-auto max-w-2xl font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("landing.footer.brand")}</h2>
            <button data-testid="footer-login-cta" onClick={() => navigate("/login")}
              className="mt-7 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-xl shadow-blue-600/25 transition-all hover:bg-blue-500">
              {t("landing.nav.login")} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-center sm:flex-row sm:text-left">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600"><ShieldCheck className="h-4 w-4 text-white" /></div>
              <span className="font-display text-sm font-bold">DispatchMe</span>
            </div>
            <p className="max-w-xl text-xs leading-relaxed text-slate-500">{t("landing.footer.disclaimer")}</p>
          </div>
          <div className="mt-6 text-center font-mono text-[11px] text-slate-600">{t("landing.footer.copyright")}</div>
        </div>
      </footer>
    </div>
  );
}

function PreviewRow({ label, value, dot, mono }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
      <span className={`flex items-center gap-2 text-sm font-semibold text-slate-100 ${mono ? "font-mono" : ""}`}>
        {dot && <span className={`h-2 w-2 rounded-full ${dot} live-dot`} />} {value}
      </span>
    </div>
  );
}

function Section({ id, title, subtitle, children }) {
  return (
    <section id={id} className="relative z-10 mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
        className="mx-auto mb-12 max-w-2xl text-center">
        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
        <p className="mt-4 text-lg text-slate-400">{subtitle}</p>
      </motion.div>
      {children}
    </section>
  );
}
